import React, { useEffect, useRef, useState } from 'react';
import { ArrowUp, ExternalLink, X } from 'lucide-react';

export interface AiHelperChatMessage {
	id: string;
	role: 'user' | 'assistant';
	content: string;
}

export interface AiHelperModalProps {
	open: boolean;
	onClose: () => void;
	messages: AiHelperChatMessage[];
	loading: boolean;
	error: string | null;
	onSendMessage: (text: string) => Promise<void>;
}

const AiHelperModal: React.FC<AiHelperModalProps> = ({
	open,
	onClose,
	messages,
	loading,
	error,
	onSendMessage,
}) => {
	const [input, setInput] = useState('');
	const messagesEndRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
	}, [messages, loading]);

	type StructuredReply = {
		answer: string;
		items: string[];
		report: { name?: string; path?: string; url?: string } | null;
		report_suggestions?: Array<{ name?: string; path?: string; url?: string }>;
		fallback?: boolean;
	};

	const parseField = (
		content: string,
		field:
			| 'Report'
			| 'Path'
			| 'Link'
			| 'Open report'
			| 'Available in'
			| 'Why this report'
			| 'How to use it'
	) => {
		const pattern = new RegExp(`(?:\\*\\*${field}:\\*\\*|${field}:)\\s*(.+)`, 'i');
		const match = content.match(pattern);
		return match?.[1]?.trim() || '';
	};

	const parseAssistantMessage = (content: string) => {
		try {
			const obj = JSON.parse(content) as StructuredReply;
			if (obj && typeof obj === 'object' && typeof obj.answer === 'string') {
				return {
					text: obj.answer.trim(),
					reportName: (obj.report?.name || '').trim(),
					path: (obj.report?.path || '').trim(),
					link: (obj.report?.url || '').trim(),
					items: Array.isArray(obj.items) ? obj.items.filter(Boolean) : [],
					reports: Array.isArray(obj.report_suggestions)
						? obj.report_suggestions
								.map((report) => ({
									name: (report?.name || '').trim(),
									path: (report?.path || '').trim(),
									url: (report?.url || '').trim(),
								}))
								.filter((report) => report.name || report.url)
								.slice(0, 3)
						: [],
				};
			}
		} catch {}

		const reportField = parseField(content, 'Report');
		const pathField = parseField(content, 'Path');
		const availableInField = parseField(content, 'Available in');
		const link =
			parseField(content, 'Open report').match(/https?:\/\/[^\s]+/i)?.[0] ||
			parseField(content, 'Link').match(/https?:\/\/[^\s]+/i)?.[0] ||
			content.match(/https?:\/\/[^\s]+/i)?.[0] ||
			'';
		let reportName = reportField;
		let path = pathField;
		if (!reportName && availableInField) {
			const match = availableInField.match(/^(.+?)\s*\((.+)\)\s*$/);
			if (match) {
				reportName = match[1].trim();
				if (!path) {
					path = match[2].trim();
				}
			} else {
				reportName = availableInField.trim();
			}
		}
		const cleaned = content
			.split('\n')
			.filter(
				(line) =>
					!/^\s*(\*\*(Report|Path|Link|Open report|Available in|Why this report|How to use it):\*\*|(Report|Path|Link|Open report|Available in|Why this report|How to use it):)/i.test(
						line
					)
			)
			.join('\n')
			.replace(/\*\*(.*?)\*\*/g, '$1')
			.replace(/https?:\/\/[^\s]+/gi, '')
			.replace(/\n{3,}/g, '\n\n')
			.trim();
		return {
			text: cleaned,
			reportName: reportName.trim(),
			path: path.trim(),
			link,
			items: [],
			reports: link || reportName ? [{ name: reportName.trim(), path: path.trim(), url: link }] : [],
		};
	};

	if (!open) {
		return null;
	}

	const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const value = input.trim();
		if (!value || loading) {
			return;
		}
		setInput('');
		await onSendMessage(value);
	};

	return (
		<div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
			<button type="button" className="absolute inset-0 bg-black/45 backdrop-blur-[1px]" aria-label="Close" onClick={onClose} />
			<div
				className="relative z-10 w-full max-w-[880px] h-[86vh] sm:h-[760px] flex flex-col rounded-[26px] bg-white border border-[#d8e1ee] shadow-[0_30px_100px_rgba(0,0,0,0.25)]"
				role="dialog"
				aria-modal="true"
				aria-labelledby="finder-modal-title"
			>
				<div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-[#e6ebf3] bg-white rounded-t-[26px]">
					<div className="flex items-center gap-3">
						<div className="leading-tight">
							<h2 id="finder-modal-title" className="text-[30px] text-[#0d223c]">
								Finder
							</h2>
							<div className="flex items-center gap-2 text-[#5a6473] text-ui">
								<span className="w-2 h-2 rounded-full bg-[#2ad17f]" />
								<span>Trained on your dashboards, metrics and definitions.</span>
							</div>
						</div>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="p-2 rounded-full text-[#6d7787] hover:bg-[#e9eef7] hover:text-[#0d223c] transition-colors"
						aria-label="Close dialog"
					>
						<X className="w-5 h-5" />
					</button>
				</div>
				<div className="px-5 py-4 overflow-y-auto flex-1 min-h-0 space-y-4 bg-white">
					{messages.map((message) => (
						<div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
							{message.role === 'assistant' ? (
								(() => {
									const parsed = parseAssistantMessage(message.content);
									return (
										<div className="max-w-[88%]">
											<div className="whitespace-pre-wrap text-[#1f2d3f] text-ui leading-relaxed px-4 py-3 rounded-3xl rounded-bl-md bg-white border border-[#dbe5f2]">
												{parsed.text}
											</div>
											{parsed.items.length > 0 && (
												<ul className="mt-3 space-y-1">
													{parsed.items.map((item, idx) => (
														<li key={`${message.id}-item-${idx}`} className="text-[#314257] text-ui leading-relaxed">
															- {item}
														</li>
													))}
												</ul>
											)}
											{(parsed.reports.length > 0 || parsed.link || parsed.reportName) && (
												<div className="mt-3 max-w-5xl">
													<div className="flex flex-nowrap gap-2 overflow-x-auto pb-1">
														{(parsed.reports.length > 0
															? parsed.reports
															: [{ name: parsed.reportName, path: parsed.path, url: parsed.link }]
														)
															.filter((report) => report.name || report.url)
															.slice(0, 3)
															.map((report, idx) =>
																report.url ? (
																	<a
																		key={`${message.id}-report-${idx}-${report.url || report.name}`}
																		href={report.url}
																		target="_blank"
																		rel="noopener noreferrer"
																		title={`Open report: ${report.name || 'report'}${report.path ? ` — ${report.path}` : ''}`}
																		className="inline-flex items-center gap-2 bg-[#f5f7fb] p-2 rounded-[36px] border border-[#f5f7fb] cursor-pointer hover:bg-[#edf1f8] transition-colors duration-200 no-underline w-fit max-w-full shrink-0"
																	>
																		<div className="flex-shrink-0">
																			<ExternalLink
																				style={{ color: '#242424' }}
																				className="w-4 h-4 flex-shrink-0"
																				strokeWidth={1.75}
																			/>
																		</div>
																		<span className="text-gray-700 text-caption whitespace-nowrap">
																			{report.name || 'Open report'}
																		</span>
																	</a>
																) : (
																	<div
																		key={`${message.id}-report-${idx}-${report.name}`}
																		className="inline-flex items-center gap-2 bg-[#f5f7fb] p-2 rounded-[36px] border border-[#f5f7fb] w-fit max-w-full shrink-0"
																	>
																		<ExternalLink
																			style={{ color: '#242424' }}
																			className="w-4 h-4 flex-shrink-0"
																			strokeWidth={1.75}
																		/>
																		<span className="text-gray-700 text-caption whitespace-nowrap">
																			{report.name}
																			{report.path ? ` — ${report.path}` : ''}
																		</span>
																	</div>
																)
															)}
													</div>
												</div>
											)}
										</div>
									);
								})()
							) : (
								<div className="max-w-[78%] whitespace-pre-wrap text-ui leading-relaxed px-4 py-3 rounded-3xl rounded-br-md bg-[#eef3fb] border border-[#dbe5f2] text-[#2f3b4a]">
									{message.content}
								</div>
							)}
						</div>
					))}
					{loading && (
						<div className="flex justify-start">
							<div className="max-w-[88%]">
								<div className="px-4 py-3 rounded-3xl rounded-bl-md bg-white border border-[#dbe5f2]">
									<div className="flex items-center gap-2">
										<span className="w-2.5 h-2.5 bg-[#9aa9bf] rounded-full animate-bounce [animation-delay:-0.2s]" />
										<span className="w-2.5 h-2.5 bg-[#9aa9bf] rounded-full animate-bounce [animation-delay:-0.1s]" />
										<span className="w-2.5 h-2.5 bg-[#9aa9bf] rounded-full animate-bounce" />
									</div>
								</div>
							</div>
						</div>
					)}
					{error && (
						<div className="text-ui text-[#b91c1c] whitespace-pre-wrap border border-[#fecaca] bg-[#fff1f2] rounded-xl px-3 py-2">
							{error}
						</div>
					)}
					<div ref={messagesEndRef} />
				</div>
				<form onSubmit={onSubmit} className="p-4 bg-white rounded-b-[26px]">
					<div className="h-14 rounded-2xl bg-white border border-[#d3ddeb] px-3 flex items-center gap-2">
						<input
							value={input}
							onChange={(e) => setInput(e.target.value)}
							placeholder="Ask about a report, metric, or filter..."
							className="flex-1 h-full bg-transparent text-[#213246] text-ui focus:outline-none placeholder:text-[#93a0b2]"
						/>
						<button
							type="submit"
							disabled={loading || !input.trim()}
							className="w-11 h-11 rounded-full bg-[#edf1f8] text-[#111827] disabled:opacity-50 disabled:pointer-events-none inline-flex items-center justify-center hover:bg-[#e5ebf5] transition-colors"
						>
							<ArrowUp className="w-5 h-5" />
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default AiHelperModal;
